import { PageElement } from '@holloway/dsl';

import {
  IconPencil,
  IconFileText,
  IconCalendar,
  IconHash,
  IconLock,
  IconPhoto,
  IconPlayerPlay,
  IconCheck,
  IconListCheck,
  IconStar,
  IconPilcrow,
  IconFolders,
  IconTable,
  IconForms,
  IconClick,
  IconSelect,
  IconPalette
} from '@tabler/icons-react';

export interface PageElementType {
  type: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
}

export interface PageElementCategory {
  id: string;
  label: string;
  icon?: React.ReactNode;
  elements: PageElementType[];
}

// Modular page element configuration - easy to extend
export const PAGE_ELEMENT_CATEGORIES: PageElementCategory[] = [
  {
    id: 'input-questions',
    label: 'Input Questions',
    icon: <IconForms size={16} />,
    elements: [
      { type: 'ShortText', label: 'Short Text', icon: <IconPencil size={16} />, description: 'Single line text input' },
      { type: 'Multiline', label: 'Multiline', icon: <IconFileText size={16} />, description: 'Multi-line text area' },
      { type: 'Date', label: 'Date', icon: <IconCalendar size={16} />, description: 'Date picker' },
      { type: 'Number', label: 'Number', icon: <IconHash size={16} />, description: 'Numeric input' },
      { type: 'Password', label: 'Password', icon: <IconLock size={16} />, description: 'Secure password input' },
      { type: 'ImageUpload', label: 'Image Upload', icon: <IconPhoto size={16} />, description: 'Image file upload' },
    ],
  },
  {
    id: 'buttons',
    label: 'Buttons',
    icon: <IconClick size={16} />,
    elements: [
      { type: 'Button', label: 'Button', icon: <IconPlayerPlay size={16} />, description: 'Navigation or action button' },
    ],
  },
  {
    id: 'selections',
    label: 'Selections',
    icon: <IconSelect size={16} />,
    elements: [
      { type: 'YesNo', label: 'Yes/No', icon: <IconCheck size={16} />, description: 'Binary choice' },
      { type: 'MultiChoice', label: 'Multi Choice', icon: <IconListCheck size={16} />, description: 'Multiple options selection' },
      { type: 'Rating', label: 'Rating', icon: <IconStar size={16} />, description: 'Star or numeric rating' },
    ],
  },
  {
    id: 'visuals',
    label: 'Visuals',
    icon: <IconPalette size={16} />,
    elements: [
      { type: 'RichText', label: 'Rich Text', icon: <IconPilcrow size={16} />, description: 'Rich text content with formatting' },
      { type: 'CollectionDisplay', label: 'Collection Display', icon: <IconFolders size={16} />, description: 'Display list of items' },
      { type: 'TableDisplay', label: 'Table Display', icon: <IconTable size={16} />, description: 'Tabular data display' },
    ],
  },
];

// Flatten all elements for easy lookup
export const ALL_PAGE_ELEMENT_TYPES: PageElementType[] = PAGE_ELEMENT_CATEGORIES.flatMap(
  (cat) => cat.elements
);

export function getPageElementInfo(type: string): PageElementType | undefined {
  return ALL_PAGE_ELEMENT_TYPES.find((el) => el.type === type);
}

// Helper to convert type to user-friendly label
function getDefaultLabel(type: string, elementNumber: number): string {
  const labelMap: Record<string, string> = {
    ShortText: 'Text Input',
    Multiline: 'Text Area',
    Date: 'Date',
    Number: 'Number',
    Password: 'Password',
    ImageUpload: 'Image Upload',
    Button: 'Button',
    YesNo: 'Yes/No Question',
    MultiChoice: 'Multiple Choice',
    Rating: 'Rating',
    RichText: 'Rich Text',
    CollectionDisplay: 'Collection',
    TableDisplay: 'Table',
  };
  const baseLabel = labelMap[type] || type;
  return elementNumber > 1 ? `${baseLabel} ${elementNumber}` : baseLabel;
}

// Create default page element by type
export function createDefaultPageElement(type: string, id: string, elementNumber: number = 1): Partial<PageElement> {
  const label = getDefaultLabel(type, elementNumber);
  // Elements bind to a variable (by GUID) lazily in the property panel; no
  // variable key is baked in at creation time.
  const base = { id, type, label };

  switch (type) {
    case 'ShortText':
      return {
        ...base,
        type: 'ShortText' as const,
        placeholder: 'Enter text',
        validation: 'none' as const,
      };
    case 'Multiline':
      return {
        ...base,
        type: 'Multiline' as const,
        placeholder: 'Enter text',
        rows: 4,
      };
    case 'Date':
      return {
        ...base,
        type: 'Date' as const,
        dateFormat: 'MM/DD/YYYY' as const,
      };
    case 'Number':
      return {
        ...base,
        type: 'Number' as const,
        min: 0,
      };
    case 'Password':
      return {
        ...base,
        type: 'Password' as const,
        minLength: 8,
        requireSpecialChar: false,
        requireNumber: false,
      };
    case 'ImageUpload':
      return {
        ...base,
        type: 'ImageUpload' as const,
        // Keep in step with MAX_BYTES / ALLOWED_FORMATS in
        // apps/api/src/assets/image-validator.ts. The DSL's Zod defaults do not
        // apply here — they only fill fields this factory omits.
        maxSizeMB: 25,
        acceptedFormats: ['jpg', 'png', 'webp', 'gif'],
        multiple: false,
      };
    case 'Button':
      return {
        ...base,
        type: 'Button' as const,
        buttonText: 'Continue',
        buttonStyle: 'primary' as const,
        buttonAlign: 'full' as const,
        action: 'navigate' as const,
      };
    case 'YesNo':
      return {
        ...base,
        type: 'YesNo' as const,
        yesLabel: 'Yes',
        noLabel: 'No',
        createBranches: false,
      };
    case 'MultiChoice':
      return {
        ...base,
        type: 'MultiChoice' as const,
        options: ['Option 1', 'Option 2', 'Option 3'],
        allowMultiple: false,
        displayStyle: 'radio' as const,
        createBranches: false,
      };
    case 'Rating':
      return {
        ...base,
        type: 'Rating' as const,
        maxRating: 5,
        ratingStyle: 'stars' as const,
      };
    case 'RichText':
      return {
        ...base,
        type: 'RichText' as const,
        content: '',
        required: false,
      };
    case 'CollectionDisplay':
      return {
        ...base,
        type: 'CollectionDisplay' as const,
        dataSource: '',
        itemTemplate: '{{item.name}}',
        displayStyle: 'list' as const,
        itemsPerPage: 10,
        required: false,
      };
    case 'TableDisplay':
      return {
        ...base,
        type: 'TableDisplay' as const,
        dataSource: '',
        columns: [
          { key: 'id', label: 'ID', sortable: true },
          { key: 'name', label: 'Name', sortable: true },
        ],
        pagination: true,
        rowsPerPage: 10,
        required: false,
      };
    default:
      return { ...base, type: 'ShortText' as const };
  }
}
